import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BlogPostInput } from '@chicare/shared';

import { api } from './api';

/**
 * Admin console data.
 *
 * Kept apart from the buyer queries because the caching rules are opposite:
 * a buyer's home feed can be a minute stale without harm, while a settings
 * screen must never show an admin a value that another admin has already
 * changed. So everything here is `staleTime: 0` and invalidates on write.
 */

export const adminKeys = {
  settings: () => ['admin', 'settings'] as const,
  settingsHistory: () => ['admin', 'settings', 'history'] as const,
  shelfLife: () => ['admin', 'shelf-life'] as const,
  shelfLifePreview: (months: number) => ['admin', 'shelf-life', 'preview', months] as const,
  pages: () => ['admin', 'pages'] as const,
  page: (slug: string) => ['admin', 'pages', slug] as const,
  media: (collection?: string) => ['admin', 'media', collection ?? 'all'] as const,
  posts: (filters: string) => ['admin', 'posts', filters] as const,
  post: (slug: string) => ['admin', 'posts', 'detail', slug] as const,
};

export function useAdminSettings() {
  return useQuery({
    queryKey: adminKeys.settings(),
    queryFn: ({ signal }) => api.adminSettings(signal),
    staleTime: 0,
  });
}

export function useSaveSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ values, reason }: { values: Record<string, unknown>; reason?: string }) =>
      api.updateAdminSettings(values, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.settings() });
      queryClient.invalidateQueries({ queryKey: adminKeys.settingsHistory() });
      queryClient.invalidateQueries({ queryKey: adminKeys.shelfLife() });
      // The buyer app reads brand and contact from /system/config.
      queryClient.invalidateQueries({ queryKey: ['system', 'config'] });
    },
  });
}

export function useSettingsHistory() {
  return useQuery({
    queryKey: adminKeys.settingsHistory(),
    queryFn: ({ signal }) => api.adminSettingsHistory(signal),
  });
}

export function useShelfLife() {
  return useQuery({
    queryKey: adminKeys.shelfLife(),
    queryFn: ({ signal }) => api.shelfLife(signal),
    staleTime: 0,
  });
}

/**
 * The impact preview. Debounced by the caller, and never fired for the value
 * already in force — asking "what if it stayed the same" is a wasted round trip.
 */
export function useShelfLifePreview(months: number | null) {
  return useQuery({
    queryKey: adminKeys.shelfLifePreview(months ?? 0),
    queryFn: ({ signal }) => api.previewShelfLife(months!, signal),
    enabled: months !== null && months > 0,
    staleTime: 30_000,
  });
}

export function useSetCategoryShelfLife() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      categoryId,
      months,
      reason,
    }: {
      categoryId: number;
      months: number | null;
      reason: string;
    }) => api.setCategoryShelfLife(categoryId, months, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: adminKeys.shelfLife() }),
  });
}

export function useRunShelfLifePass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dryRun, reason }: { dryRun: boolean; reason?: string }) =>
      api.runShelfLifePass(dryRun, reason),
    onSuccess: (_data, variables) => {
      if (!variables.dryRun) {
        queryClient.invalidateQueries({ queryKey: adminKeys.shelfLife() });
      }
    },
  });
}

export function useAdminPages() {
  return useQuery({
    queryKey: adminKeys.pages(),
    queryFn: ({ signal }) => api.adminPages(signal),
    staleTime: 0,
  });
}

export function useAdminPage(slug: string | undefined) {
  return useQuery({
    queryKey: adminKeys.page(slug ?? ''),
    queryFn: ({ signal }) => api.adminPage(slug!, signal),
    enabled: Boolean(slug),
    staleTime: 0,
  });
}

export function useSavePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slug, page }: { slug: string | null; page: Record<string, unknown> }) =>
      api.saveAdminPage(slug, page),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminKeys.pages() });
      if (variables.slug) {
        queryClient.invalidateQueries({ queryKey: adminKeys.page(variables.slug) });
      }
    },
  });
}

// ---------------------------------------------------------------------------
// The blog
//
// Separate hooks from the pages above, and separate cache keys, because they
// are separate permissions: a content writer can hold `posts.write` and be
// refused `/admin/pages` entirely. Sharing a key would mean one 403 emptying
// the other screen.
// ---------------------------------------------------------------------------

export function useAdminPosts(filters: { status?: string; category?: string; q?: string } = {}) {
  return useQuery({
    queryKey: adminKeys.posts(JSON.stringify(filters)),
    queryFn: ({ signal }) => api.adminPosts(filters, signal),
    staleTime: 0,
  });
}

export function useAdminPost(slug: string | undefined) {
  return useQuery({
    queryKey: adminKeys.post(slug ?? ''),
    queryFn: ({ signal }) => api.adminPost(slug!, signal),
    enabled: Boolean(slug),
    staleTime: 0,
  });
}

export function useSavePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slug, post }: { slug: string | null; post: BlogPostInput }) =>
      api.saveAdminPost(slug, post),
    onSuccess: (saved, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'posts'] });
      if (variables.slug) {
        queryClient.invalidateQueries({ queryKey: adminKeys.post(variables.slug) });
      }
      // The slug can change on a title edit, so the NEW one is invalidated too
      // — otherwise the editor reopens on a cached copy of the old row.
      if (saved?.slug && saved.slug !== variables.slug) {
        queryClient.invalidateQueries({ queryKey: adminKeys.post(saved.slug) });
      }
    },
  });
}

export function useDeletePost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slug, reason }: { slug: string; reason?: string }) =>
      api.deleteAdminPost(slug, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'posts'] }),
  });
}

export function useRestorePostRevision() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slug, version }: { slug: string; version: number }) =>
      api.restorePostRevision(slug, version),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'posts'] });
      queryClient.invalidateQueries({ queryKey: adminKeys.post(variables.slug) });
    },
  });
}

export function useUploadMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, collection, alt }: { file: File; collection: string; alt?: string }) =>
      api.uploadMedia(file, collection, alt),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'media'] }),
  });
}

// ---------------------------------------------------------------------------
// Refund review & collections
// ---------------------------------------------------------------------------

export const moneyKeys = {
  refunds: (status: string) => ['admin', 'refunds', status] as const,
  refund: (reference: string) => ['admin', 'refunds', 'detail', reference] as const,
  debts: (status: string) => ['admin', 'seller-debts', status] as const,
  debt: (pharmacyId: number) => ['admin', 'seller-debts', pharmacyId] as const,
};

export function useRefundClaims(status = 'open') {
  return useQuery({
    queryKey: moneyKeys.refunds(status),
    queryFn: ({ signal }) => api.refundClaims(status, signal),
    // A claim decided by a colleague thirty seconds ago must not still look
    // open on your screen — two people working the same queue is the normal
    // case, not the edge case.
    staleTime: 0,
    refetchInterval: 60_000,
  });
}

export function useRefundClaim(reference: string | null) {
  return useQuery({
    queryKey: moneyKeys.refund(reference ?? ''),
    queryFn: ({ signal }) => api.refundClaim(reference as string, signal),
    enabled: reference !== null,
    staleTime: 0,
  });
}

export function useApproveRefund() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      reference,
      note,
      amountKobo,
      idempotencyKey,
    }: {
      reference: string;
      note?: string;
      amountKobo?: number;
      idempotencyKey: string;
    }) => api.approveRefund(reference, { note, amountKobo }, idempotencyKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'refunds'] });
      // An approval outside the window raises a debt, so collections changes too.
      queryClient.invalidateQueries({ queryKey: ['admin', 'seller-debts'] });
    },
  });
}

export function useDeclineRefund() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reference, note }: { reference: string; note: string }) =>
      api.declineRefund(reference, note),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'refunds'] }),
  });
}

export function useSellerDebts(status = 'open') {
  return useQuery({
    queryKey: moneyKeys.debts(status),
    queryFn: ({ signal }) => api.sellerDebts(status, signal),
    staleTime: 0,
  });
}

export function useSellerDebt(pharmacyId: number | null) {
  return useQuery({
    queryKey: moneyKeys.debt(pharmacyId ?? 0),
    queryFn: ({ signal }) => api.sellerDebt(pharmacyId as number, signal),
    enabled: pharmacyId !== null,
    staleTime: 0,
  });
}

export function useRecordDebtPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pharmacyId,
      amountKobo,
      reference,
      note,
      idempotencyKey,
    }: {
      pharmacyId: number;
      amountKobo: number;
      reference: string;
      note?: string;
      idempotencyKey: string;
    }) => api.recordDebtPayment(pharmacyId, { amountKobo, reference, note }, idempotencyKey),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'seller-debts'] }),
  });
}
